import {
  FEATURE_KEYS_CATALOG,
  PERMISSION_AREA_KEYS,
  PERMISSION_SCOPE_KEYS,
  type FeatureKey,
  type MyContext,
  type OrganizationRole,
  type PermissionAction,
  type PermissionArea,
  type PermissionScope,
  type RolesResponse,
  type UpdateRoleBody,
} from "@gioia365/contracts";
import type { Translate } from "../../i18n/locale";
import { ACTION_LABEL, AREA_LABEL, CREATE_SCOPE_LABEL, FEATURE_GROUPS, FEATURE_LABEL, SCOPE_LABEL, areaActions } from "./permission-labels";
import { lowerFirst } from "./roles-in-words";

// La bozza di un ruolo (piano M1b Task 9, D9): l'editor lavora su una copia locale e la salva in una volta sola.
// Qui c'è soltanto la logica pura: la bozza da un ruolo salvato o da un ruolo di partenza, le modifiche dette a
// parole, i problemi che impediscono di salvare, il corpo della `PATCH` e ciò che chi modifica può dare. Per la
// decisione del product owner sulla questione 38 (30 settembre 2026) nessuno dà funzioni o ambiti che non ha, salvo
// il Super admin dell'azienda e il Super admin di Gioia365: il server lo controlla comunque (`withinPowers`), qui
// serve a non offrire ciò che il server rifiuterebbe.

/** La matrice di un ruolo con ogni cella scritta: le aree di sola lettura hanno soltanto «vedere». */
export type AreaPermissions = Record<PermissionArea, Partial<Record<PermissionAction, PermissionScope>>>;

export type RoleLevel = 1 | 2 | 3 | 4;

export type RoleDraft = {
  /** `null` finché il ruolo non esiste sul server. */
  id: string | null;
  /** Il ruolo di partenza: il server copia da lì livello, permessi e funzioni (`copyFromRoleId` è obbligatorio). */
  copyFromRoleId: string;
  name: string;
  description: string | null;
  /** Ereditato dal ruolo di partenza, non si modifica. */
  level: RoleLevel;
  areaPermissions: AreaPermissions;
  features: string[];
  /** «Ruolo su misura»: la persona che lo riceve quando si salva. */
  assignTo: { membershipId: string; displayName: string } | null;
};

const LEVELS: readonly RoleLevel[] = [1, 2, 3, 4];
const SCOPE_RANK: Readonly<Record<PermissionScope, number>> = { none: 0, own: 1, all: 2 };

/** Le scelte di una cella della matrice: «Creare» soltanto «No» e «Sì». */
export function scopesFor(action: PermissionAction): readonly PermissionScope[] {
  return action === "create" ? ["none", "all"] : PERMISSION_SCOPE_KEYS;
}

/** Se `scope` dà più di `limit` (`none` < `own` < `all`). */
export function widerThan(scope: PermissionScope, limit: PermissionScope): boolean {
  return SCOPE_RANK[scope] > SCOPE_RANK[limit];
}

/** Ciò che si mostra in una cella: «No», «Solo i suoi», «Tutti»; per «Creare» «No» e «Sì». */
export function scopeLabel(t: Translate, action: PermissionAction, scope: PermissionScope): string {
  return action === "create" ? t(CREATE_SCOPE_LABEL[scope === "all" ? "all" : "none"]) : t(SCOPE_LABEL[scope]);
}

/** L'ambito salvato, ricondotto alle scelte della cella; assente o sconosciuto vale «No», come per il server. */
function savedScope(saved: OrganizationRole["areaPermissions"], area: PermissionArea, action: PermissionAction): PermissionScope {
  const actions = Object.hasOwn(saved, area) ? saved[area] : undefined;
  const value = actions && Object.hasOwn(actions, action) ? actions[action] : undefined;
  const scope = PERMISSION_SCOPE_KEYS.find((candidate) => candidate === value) ?? "none";
  return scopesFor(action).includes(scope) ? scope : "none";
}

function matrixOf(saved: OrganizationRole["areaPermissions"]): AreaPermissions {
  const matrix = {} as AreaPermissions;
  for (const area of PERMISSION_AREA_KEYS) {
    matrix[area] = Object.fromEntries(areaActions(area).map((action) => [action, savedScope(saved, area, action)]));
  }
  return matrix;
}

/** Lo stesso ambito che la matrice mostra: una cella non scritta vale «No». */
export function cellOf(matrix: AreaPermissions, area: PermissionArea, action: PermissionAction): PermissionScope {
  return matrix[area][action] ?? "none";
}

const levelOf = (level: number): RoleLevel => LEVELS.find((candidate) => candidate === level) ?? 1;

/** Una descrizione vuota o di soli spazi non c'è. */
export function cleanDescription(description: string | null): string | null {
  const trimmed = description?.trim();
  return trimmed ? trimmed : null;
}

export function draftFromRole(role: OrganizationRole): RoleDraft {
  return {
    id: role.id,
    copyFromRoleId: role.createdFromRoleId ?? role.id,
    name: role.name,
    description: role.description,
    level: levelOf(role.level),
    areaPermissions: matrixOf(role.areaPermissions),
    features: [...role.features],
    assignTo: null,
  };
}

/** Un ruolo nuovo, duplicato o su misura: parte da `from` e non esiste finché non si salva. */
export function newRoleDraft(from: OrganizationRole, name: string, description: string | null, assignTo: RoleDraft["assignTo"] = null): RoleDraft {
  return { ...draftFromRole(from), id: null, copyFromRoleId: from.id, name, description, assignTo };
}

const isSettings = (key: string) => key.startsWith("settings.");

/**
 * Le modifiche della bozza rispetto al ruolo salvato, una frase ciascuna: nome, descrizione, poi le celle della
 * matrice nell'ordine del catalogo («Lead, vedere: da «Tutti» a «Solo i suoi».»), poi le funzioni nell'ordine dei
 * gruppi («Può: …», «Non può più: …»). Un ruolo non ancora salvato (`saved` nullo) è una modifica sola.
 */
export function diffRole(saved: RoleDraft | null, draft: RoleDraft, t: Translate): string[] {
  const name = draft.name.trim();
  if (!saved) return [draft.assignTo ? t("roles.diff.newFor", { name, person: draft.assignTo.displayName }) : t("roles.diff.new", { name })];
  const changes: string[] = [];
  if (saved.name.trim() !== name) changes.push(t("roles.diff.name", { before: saved.name.trim(), after: name }));
  if (cleanDescription(saved.description) !== cleanDescription(draft.description)) changes.push(t("roles.diff.description"));
  for (const area of PERMISSION_AREA_KEYS) {
    for (const action of areaActions(area)) {
      const before = cellOf(saved.areaPermissions, area, action);
      const after = cellOf(draft.areaPermissions, area, action);
      if (before === after) continue;
      changes.push(
        t("roles.diff.scope", {
          area: t(AREA_LABEL[area]),
          action: lowerFirst(t(ACTION_LABEL[action])),
          before: scopeLabel(t, action, before),
          after: scopeLabel(t, action, after),
        }),
      );
    }
  }
  const had = new Set(saved.features);
  const has = new Set(draft.features);
  for (const key of FEATURE_GROUPS.flatMap((group) => group.keys)) {
    if (had.has(key) === has.has(key)) continue;
    const label = t(FEATURE_LABEL[key]);
    if (isSettings(key)) changes.push(t(has.has(key) ? "roles.diff.canSetting" : "roles.diff.cannotSetting", { section: label }));
    else changes.push(t(has.has(key) ? "roles.diff.can" : "roles.diff.cannot", { feature: lowerFirst(label) }));
  }
  return changes;
}

/**
 * Ciò che impedisce di salvare il nome: vuoto, oppure già di un altro ruolo non archiviato. Il confronto ignora
 * maiuscole e spazi (il server confronta il nome esatto): «admin» accanto ad «Admin» confonderebbe chi sceglie.
 */
export function draftProblem(draft: RoleDraft, others: readonly OrganizationRole[]): "name_missing" | "name_taken" | null {
  const name = draft.name.trim().toLocaleLowerCase();
  if (!name) return "name_missing";
  const taken = others.some((role) => role.archivedAt === null && role.id !== draft.id && role.name.trim().toLocaleLowerCase() === name);
  return taken ? "name_taken" : null;
}

/** Il Super admin dell'azienda e il Super admin di Gioia365 non hanno il limite dei propri poteri. */
const unlimited = (context: MyContext) => context.kind === "platform" || context.role?.isSystem === true;

/**
 * Le funzioni che chi modifica può mettere in un ruolo: quelle che la piattaforma lascia dare alle aziende e,
 * salvo il Super admin dell'azienda e la piattaforma, che ha anche lui (questione 38: un delegato dà soltanto ciò
 * che ha). Nell'ordine del catalogo.
 */
export function grantableFeatures(context: MyContext, catalog: RolesResponse["catalog"]): Set<string> {
  const held = new Set(context.features);
  return new Set(
    catalog.features.filter((entry) => entry.availableToOrganizations && (unlimited(context) || held.has(entry.key))).map((entry) => entry.key),
  );
}

/** L'ambito più largo che chi modifica può dare su un'area e un'azione: il suo, salvo Super admin e piattaforma. */
export function scopeLimit(context: MyContext, area: PermissionArea, action: PermissionAction): PermissionScope {
  if (unlimited(context)) return "all";
  const actions = Object.hasOwn(context.areaPermissions, area) ? context.areaPermissions[area] : undefined;
  return (actions && Object.hasOwn(actions, action) ? actions[action] : undefined) ?? "none";
}

/** Le funzioni da mandare: soltanto quelle che la piattaforma lascia dare, nell'ordine del catalogo. */
function featuresToSend(features: readonly string[], available: ReadonlySet<string>): FeatureKey[] {
  return FEATURE_KEYS_CATALOG.filter((key) => features.includes(key) && available.has(key));
}

/** La matrice intera come la vuole il server: `areaPermissions` sostituisce quella salvata. */
function matrixToSend(matrix: AreaPermissions): Record<string, Record<string, string>> {
  return Object.fromEntries(
    PERMISSION_AREA_KEYS.map((area) => [area, Object.fromEntries(areaActions(area).map((action) => [action, cellOf(matrix, area, action)]))]),
  );
}

function sameMatrix(first: AreaPermissions, second: AreaPermissions): boolean {
  return PERMISSION_AREA_KEYS.every((area) => areaActions(area).every((action) => cellOf(first, area, action) === cellOf(second, area, action)));
}

/**
 * Il corpo della `PATCH /api/roles/:id` con i soli campi cambiati (`null` se non cambia nulla): nome e descrizione
 * ripuliti dagli spazi, la matrice intera e le funzioni, che sostituiscono quelle salvate.
 */
export function updateBody(saved: RoleDraft, draft: RoleDraft, available: ReadonlySet<string>): UpdateRoleBody | null {
  const body: UpdateRoleBody = {};
  const name = draft.name.trim();
  if (name !== saved.name.trim()) body.name = name;
  const description = cleanDescription(draft.description);
  if (description !== cleanDescription(saved.description)) body.description = description;
  if (!sameMatrix(saved.areaPermissions, draft.areaPermissions)) body.areaPermissions = matrixToSend(draft.areaPermissions);
  const features = featuresToSend(draft.features, available);
  if (features.join() !== featuresToSend(saved.features, available).join()) body.features = features;
  return Object.keys(body).length > 0 ? body : null;
}
